import { AbstractBlockFactory } from '../block/AbstractBlockFactory';
import {
    ComponentSettingsChartMeasureField,
    ComponentSettingsLineChartAxis,
    LineChartSeriesType,
    NewBlockContextType,
    BlockType,
    DatasetField,
    fieldToChartMeasuredField,
    LineChartInitialDataResponse,
    MeasureFieldAggregationType,
    DatasetFieldValueType,
} from '@report-content';
import { YNValues } from 'tools/types';
import { ChartComponentSettings, AllowedMeasureFieldAggregations } from './line-chart';

export const KpiAllowedMeasureFieldAggregations: Array<MeasureFieldAggregationType> = [
    { label: 'None', name: 'None', value_type: 'numeric' as DatasetFieldValueType },
].concat(AllowedMeasureFieldAggregations);

export class BlockFactoryKpiChart extends AbstractBlockFactory<ComponentSettingsKpiChart> {
    public getNewBlock({
        nextUId,
        nextSortOrder,
        datasetFields,
    }: NewBlockContextType): BlockType<ComponentSettingsKpiChart> {
        const uid = 'block_uid_' + nextUId;
        return {
            // TODO:
            component: {
                datasetRendererBlockComponentId: nextUId,
                datasetRendererBlockId: nextUId,
                datasetRendererComponentId: nextUId,
                displayOrder: nextSortOrder,
                internalName: 'kpi_chart',
                settings: {
                    uid: uid,
                    title: '',
                    height: 600,
                    show_in_viewer: 'Y',
                    is_preview_ind: 'N',
                    include_in_email: 'Y',
                    is_collapsed_ind: 'N',
                    include_in_email_attachment: 'Y',
                    internal_name: 'kpi_chart',
                    groupFilter: { operator: 'AND', rules: [] },
                    queryAgainstEntireDataset: 'N',
                    dataQueries: {
                        table: {
                            resultFieldAggregations: {},
                            resultFields: [],
                            sort: [],
                        },
                    },
                    display_label: 'N',
                    measureFields: [],
                    series: 'single',
                    x_axis: {
                        field: '',
                        title: '',
                        value_type: 'numeric',
                        defaultZoom: '',
                        display_mask_id: '0',
                    },
                    y_axis: [{ field: '', title: '', value_type: 'numeric', defaultZoom: '', display_mask_id: '0' }],
                    isOnCreation: true,
                    stoplight: {
                        includeStoplight: 'N',
                        stoplightMoreIsBetter: 'Y',
                        stoplightCalcMethod: 'fixed range',
                        stoplightColorForHighValues: '#077E45',
                        stoplightColorForLowValues: '#AC2B2B',
                        stoplightOkRangeStart: 0,
                        stoplightOkRangeStartComparisonType: 'strict',
                        stoplightOkRangeEnd: 0,
                        stoplightOkRangeEndComparisonType: 'strict',
                        stoplightCompareValueCalcMethodId: 0,
                        stoplightCompareValuePeriod: 0,
                        stoplightCompareValueUnit: 'months',
                        stoplightTargetId: 0,
                    },
                    kpiVariance: {
                        includeKpiVariance: 'N',
                        varianceCompareValuePeriod: 0,
                        varianceCompareValueUnit: 'months',
                        showVarianceAs: 'percentage',
                    },
                    alertRules: [],
                },
            },
            // datasetRendererBlockId: number;
            displayOrder: nextSortOrder,
            // elementId: number;
            uid: uid,
        };
    }
}

export type StoplightComparisonType = 'strict' | 'non strict';
export type CalcMethodType = 'fixed range' | 'prior values' | 'target comparison';
export interface IKpiChartStoplightSettings {
    includeStoplight: YNValues;
    stoplightMoreIsBetter: YNValues;
    stoplightCalcMethod: CalcMethodType;
    stoplightColorForHighValues: string;
    stoplightColorForLowValues: string;
    stoplightOkRangeStart: number;
    stoplightOkRangeStartComparisonType: StoplightComparisonType;
    stoplightOkRangeEnd: number;
    stoplightOkRangeEndComparisonType: StoplightComparisonType;
    stoplightCompareValueCalcMethodId: number;
    stoplightCompareValuePeriod: number;
    stoplightCompareValueUnit: string;
    stoplightTargetId: number;
}

export interface IKpiChartVarianceSettings {
    includeKpiVariance: YNValues;
    varianceCompareValuePeriod: number;
    varianceCompareValueUnit: string;
    showVarianceAs: 'percentage' | 'absolute';
}

export interface IKpiAlertRule {
    report_alert_rule_id: number;
    name: string;
}

export interface ComponentSettingsKpiChart extends ChartComponentSettings {
    uid: string;
    display_label: YNValues;
    measureFields: Array<ComponentSettingsKpiChartMeasureField>;
    queryAgainstEntireDataset: YNValues;
    series: LineChartSeriesType;
    x_axis: ComponentSettingsLineChartAxis;
    y_axis?: Array<ComponentSettingsLineChartAxis>;
    stoplight: IKpiChartStoplightSettings;
    kpiVariance: IKpiChartVarianceSettings;
    alertRules: Array<IKpiAlertRule>;
}

export interface ComponentSettingsKpiChartMeasureField extends ComponentSettingsChartMeasureField {
    is_primary: YNValues;
    overlay_in_tile: YNValues;
    overlay_in_chart: YNValues;
}

export function fieldToKpiChartMeasuredField(field: DatasetField): ComponentSettingsKpiChartMeasureField {
    const chartMeasure = fieldToChartMeasuredField(field);
    return {
        ...chartMeasure,
        is_primary: 'N',
        overlay_in_tile: 'N',
        overlay_in_chart: 'N',
    } as ComponentSettingsKpiChartMeasureField;
}

export interface IKpiResults {
    [key: string]: number | string | null;
    stoplight_bad_threshold: number | null;
    stoplight_good_threshold: number | null;
    moving_avg: number | null;
    standard_deviation: number | null;
}

export interface KpiChartInitialDataResponse extends LineChartInitialDataResponse {
    kpiResults: IKpiResults[];
    alertEvents: IKpiAlertEvent[];
}

// If Snapshot Date is used as a measurement time, the result_data will be empty.
// Use measurement_time, title and filters as Alert Event data.
// If other date column is used as measurement time, the result_data will be used to get the Alert Event data,
// and measurement_time, title and filters will be ignored.
export interface IKpiAlertEvent {
    measurement_time: string;
    title: string;
    filters: IKpiAlertEventFilters;
    result_data: IKpiAlertEventResultData[];
}

export interface IKpiAlertEventResultData {
    title: string;
    row: IKpiAlertEventFilters;
}

export interface IKpiAlertEventFilters {
    [key: string]: number | string;
}
